import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ButtonLoader } from '@/components/button-loader';
import { useGroups } from '@/store/group';
import { useUsers } from '@/store/user';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';

export const CreateGroupPage = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [ownerId, setOwnerId] = useState('');
    const allUsers = useUsers((state) => state.users);
    const listUsers = useUsers((state) => state.list);
    const createGroup = useGroups((state) => state.create);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await createGroup({ name, description, owner_ids: ownerId ? [ownerId] : [] } as any);
            toast.success('Group created successfully');
            navigate('/groups');
        } catch (err) {
            console.error(err);
            toast.error('Failed to create group. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        setBreadcrumbs([{ name: 'Groups', to: '/groups' }, { name: 'Create' }]);
        listUsers().catch(console.error);
    }, [listUsers, setBreadcrumbs]);

    return (
        <PageLayout title="Create Group" description="Register a new user group.">
            <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6 md:p-8">
                
                <form onSubmit={submit} className="space-y-6">
                    <div className="grid gap-6 md:grid-cols-2">
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Name</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="e.g. Administrators"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Description</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="Platform administrators group"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Owner</label>
                            <select
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white w-full"
                                value={ownerId}
                                onChange={(e) => setOwnerId(e.target.value)}
                            >
                                <option value="" disabled>(Select owner...)</option>
                                <option value="">None</option>
                                {allUsers?.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.email}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Leave unselected if no initial owner should be assigned.</p>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                        <Link
                            to="/groups"
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center min-w-35 gap-2"
                        >
                            {isSubmitting ? <ButtonLoader /> : 'Create Group'}
                        </button>
                    </div>
                </form>
            </div>
        </PageLayout>
    );
};
